import ReactSelect, { type Props as RSProps, type GroupBase, type StylesConfig } from "react-select"

export type SelectOption<V = string | number> = { value: V; label: string }

// Wide style typing — the same dark-surface tokens render correctly for
// both single- and multi-select variants. Using `any` for the IsMulti
// generic dimension here keeps the styles object reusable across both
// `<Select>` and `<MultiSelect>` without re-declaring 50 lines.
// eslint-disable-next-line @typescript-eslint/no-explicit-any

// Thin wrapper around react-select wired to our dark-surface tokens. Use this
// instead of <select> for any picker on the portal — the native control reads
// as a flat button on dark backgrounds and clips long option lists.
//
// Usage:
//   <Select
//     value={opts.find(o => o.value === id) ?? null}
//     onChange={(o) => o && setId(o.value)}
//     options={opts}
//   />

const styles: StylesConfig<SelectOption<string | number>, boolean, GroupBase<SelectOption<string | number>>> = {
  control: (base, state) => ({
    ...base,
    // Bumped from 38px to 44px — clears the WCAG touch-target minimum so
    // dropdowns are tappable on phones without obstructing the option
    // list. Border radius bumped to 12px to match the new Input/Textarea
    // `rounded-xl` look. The focus shadow uses var(--brand-red-soft) for
    // a subtle red ring, matching <Input>'s focus treatment.
    minHeight: 44,
    backgroundColor: "var(--surface-panel-2, #1a1a1f)",
    borderColor: state.isFocused ? "var(--brand-red)" : "var(--border-soft, #2a2a30)",
    borderRadius: 12,
    boxShadow: state.isFocused
      ? "0 0 0 3px var(--brand-red-soft, rgba(225, 6, 19, 0.12))"
      : "none",
    cursor: "pointer",
    transition: "border-color 120ms ease, box-shadow 120ms ease",
    ":hover": { borderColor: "var(--border-strong, #3a3a42)" },
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 10px" }),
  singleValue: (base) => ({ ...base, color: "var(--text-app, #e6e6ea)", fontSize: 14, fontWeight: 500 }),
  input: (base) => ({ ...base, color: "var(--text-app, #e6e6ea)" }),
  placeholder: (base) => ({ ...base, color: "var(--text-subtle, #6b6b75)" }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base, state) => ({
    ...base,
    color: state.isFocused ? "var(--text-app, #e6e6ea)" : "var(--text-muted, #9a9aa3)",
    padding: "0 8px",
    ":hover": { color: "var(--text-app, #e6e6ea)" },
  }),
  clearIndicator: (base) => ({ ...base, color: "var(--text-muted, #9a9aa3)" }),
  menu: (base) => ({
    ...base,
    backgroundColor: "var(--surface-panel-2, #1a1a1f)",
    border: "1px solid var(--border-soft, #2a2a30)",
    borderRadius: 8,
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    marginTop: 4,
    zIndex: 30,
  }),
  menuList: (base) => ({ ...base, padding: 4 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "rgba(224, 69, 91, 0.18)"
      : state.isFocused
        ? "rgba(255,255,255,0.05)"
        : "transparent",
    color: "var(--text-app, #e6e6ea)",
    fontSize: 14,
    borderRadius: 6,
    padding: "8px 10px",
    cursor: "pointer",
    ":active": { backgroundColor: "rgba(224, 69, 91, 0.24)" },
  }),
  noOptionsMessage: (base) => ({ ...base, color: "var(--text-muted, #9a9aa3)", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: "rgba(224, 69, 91, 0.18)",
    borderRadius: 6,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "var(--text-app, #e6e6ea)",
    fontSize: 13,
    padding: "2px 6px",
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "var(--text-muted, #9a9aa3)",
    ":hover": {
      backgroundColor: "rgba(224, 69, 91, 0.32)",
      color: "var(--text-app, #e6e6ea)",
    },
  }),
}

export function Select<V extends string | number = string | number>(
  props: RSProps<SelectOption<V>, false, GroupBase<SelectOption<V>>>,
) {
  return (
    <ReactSelect<SelectOption<V>, false, GroupBase<SelectOption<V>>>
      {...props}
      styles={styles as unknown as StylesConfig<SelectOption<V>, false, GroupBase<SelectOption<V>>>}
      classNamePrefix="rs"
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
    />
  )
}

// Multi-select variant for fields like agent-pool membership pickers
// (Settings → Team) and any future "tag a row with N options". Same
// dark-surface tokens; react-select's IsMulti generic is `true`.
export function MultiSelect<V extends string | number = string | number>(
  props: RSProps<SelectOption<V>, true, GroupBase<SelectOption<V>>>,
) {
  return (
    <ReactSelect<SelectOption<V>, true, GroupBase<SelectOption<V>>>
      {...props}
      isMulti
      styles={styles as unknown as StylesConfig<SelectOption<V>, true, GroupBase<SelectOption<V>>>}
      classNamePrefix="rs"
      menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
    />
  )
}
